// TenantDetailPage — Phase 6 first surface (Wave 2.7).
//
// Shows the rich v2 TenantDetail response: provisioning workflow info,
// resource allocations timeline (step-by-step view of what the saga has
// allocated), plan + topology summary, and lifecycle action availability.
//
// Polls `/provisioning-status` while the tenant is in `pending` state so
// the user sees the saga progress through resource allocations live.
//
// Endpoints consumed:
//
//   GET /api/v1/organizations/{id}/tenants/{tenantId}                — TenantDetail
//   GET /api/v1/organizations/{id}/tenants/{tenantId}/provisioning-status
//   GET /api/v1/organizations/{id}/tenants/{tenantId}/resources       — fallback if detail.resources is empty

import type { components } from '@intelliauth/api-client-v2-cp/types'
import { Badge, Button, Card } from '@intelliauth/ui'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Database,
  ExternalLink,
  Globe,
  Loader2,
  PauseCircle,
  PlayCircle,
  RotateCw,
  Server,
  Trash2,
  Workflow,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'
import { tenantBaseUrl } from '@/lib/domain'

type TenantDetail = components['schemas']['TenantDetail']
type ProvisioningSummary = components['schemas']['ProvisioningSummary']
type ResourceAllocation = components['schemas']['ResourceAllocation']

// ── Provisioning status (timeline) wire shape ──
//
// Matches the JSON returned by /api/v1/organizations/{id}/tenants/{tenantId}/provisioning-status
// when the BE has the cpEventRepo wired (always in prod). The shape is
// declared inline here until `make openapi-cp` catches up; once the
// generated types carry it we replace this with the components alias.
//
// timeline[] is one entry per provisioning attempt (= one Temporal
// workflow execution). On retry the BE appends a new attempt; the FE
// expands the latest and collapses earlier ones.
type TimelineStep = {
  step_name: string
  event_type: string
  timestamp: string
  severity?: string
  error?: string
}
type TimelineStage = {
  stage: string // stable id: 'validating' | 'reserving_capacity' | ...
  label: string // admin-facing copy from BE displaylabel.go
  subtext: string
  status: 'pending' | 'in_flight' | 'done' | 'failed' | 'compensating'
  started_at?: string
  ended_at?: string
  steps: TimelineStep[]
}
type TimelineAttempt = {
  attempt_number: number
  workflow_id: string
  state: 'running' | 'completed' | 'failed'
  started_at: string
  ended_at?: string
  stages: TimelineStage[]
}
type ProvisioningStatus = {
  tenant_id: string
  status: string
  workflow?: { id?: string; state?: string; started_at?: string; ended_at?: string }
  timeline?: TimelineAttempt[]
  last_error?: string
  attempt_count?: number
  actions?: { can_retry?: boolean; can_reset?: boolean; can_cancel?: boolean }
}
// Recreate + Delete hint booleans landed in Phase B+C of the lifecycle
// work but aren't in the generated types yet. Augment defensively until
// `make openapi-cp` regenerates the schema.
type ActionAvailability = components['schemas']['ActionAvailability'] & {
  can_recreate?: boolean
  can_delete?: boolean
}

// Status label map covers every state the backend can return. The
// OpenAPI enum currently says {pending, active, suspended, deleted}
// but the live cp-api also emits `decommissioned` (terminal) and
// `failed` / `provisioning` / `decommissioning` (in-progress). Fall
// back to a neutral "Unknown" badge instead of mislabeling new states
// as "Provisioning".
const STATUS_LABEL: Record<string, { label: string; bg: string; text: string }> = {
  active: { label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  pending: { label: 'Provisioning', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  provisioning: { label: 'Provisioning', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  suspended: { label: 'Suspended', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  failed: { label: 'Failed', bg: 'bg-red-500/20', text: 'text-red-400' },
  decommissioning: { label: 'Decommissioning', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  decommissioned: { label: 'Decommissioned', bg: 'bg-gray-500/20', text: 'text-gray-400' },
  deleted: { label: 'Archived', bg: 'bg-gray-500/20', text: 'text-gray-400' },
}

const UNKNOWN_STATUS = { label: 'Unknown', bg: 'bg-gray-500/20', text: 'text-gray-400' }

const IN_PROGRESS_STATUSES = new Set(['pending', 'provisioning', 'decommissioning'])

// Display order for resource types so the timeline reads top-down in a
// recognizable flow regardless of allocation order.
const RESOURCE_TYPE_ORDER: Record<string, number> = {
  postgres_database: 1,
  temporal_namespace: 2,
  k8s_namespace: 3,
  hydra_client: 4,
  aegis_identity: 5,
}

// Resource labels are the admin-facing names — no Postgres / Temporal /
// Aegis surface terms. The internal resource_type strings stay machine-
// readable (operators read them via cp-api logs); these are what shows
// in the "Allocated resources" rail on the tenant detail page.
//
// See cp/internal/orchestrator/provisiontenant/displaylabel.go for the
// matching saga-step grouping — same translation principle: technical
// truth at the data layer, friendly copy at the render layer.
const RESOURCE_TYPE_LABEL: Record<string, string> = {
  postgres_database: 'Secure data store',
  temporal_namespace: 'Workflow capacity',
  k8s_namespace: 'Tenant infrastructure',
  hydra_client: 'OAuth integration',
  aegis_identity: 'Tenant security profile',
}

function readErr(err: unknown, fallback: string): string {
  if (err instanceof CpV2ApiError) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

export default function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()
  const { currentOrg } = useAuth()

  const [tenant, setTenant] = useState<TenantDetail | null>(null)
  // provStatus carries the stage-grouped timeline. Lives separately
  // from `tenant` so the timeline can refresh on its own poll cadence
  // without re-rendering the whole tenant card. Optional fields used
  // by the new ProvisioningTimeline:
  //   .timeline: TimelineAttempt[] — present once the BE wires the
  //              cpEventRepo (always in prod; absent in older test
  //              boots — FE falls back to the legacy allocations
  //              render below in that case).
  //   .last_error, .attempt_count — surface the failure banner.
  const [provStatus, setProvStatus] = useState<ProvisioningStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetch = useCallback(async () => {
    if (!currentOrg || !tenantId) return
    try {
      // Fire the two reads in parallel — they're independent. The
      // provisioning-status call is best-effort: a failure there must
      // NOT block the main tenant card from rendering. We swallow the
      // error and leave `provStatus` as whatever was there before
      // (or null on first load).
      const [tenantRes, statusRes] = await Promise.allSettled([
        cpClient().get('/api/v1/organizations/{id}/tenants/{tenantId}', {
          path: { id: currentOrg.id, tenantId },
        }),
        // biome-ignore lint/suspicious/noExplicitAny: provisioning-status not in generated types yet
        (cpClient() as any).get(
          '/api/v1/organizations/{id}/tenants/{tenantId}/provisioning-status',
          { path: { id: currentOrg.id, tenantId } },
        ),
      ])
      if (tenantRes.status === 'fulfilled') {
        const data = (tenantRes.value as { data: TenantDetail }).data
        setTenant(data)
      } else {
        throw tenantRes.reason
      }
      if (statusRes.status === 'fulfilled') {
        const data = (statusRes.value as { data: ProvisioningStatus }).data
        setProvStatus(data)
      }
    } catch (err) {
      toast.error(readErr(err, 'Failed to load tenant'))
    } finally {
      setLoading(false)
    }
  }, [currentOrg, tenantId])

  useEffect(() => {
    fetch()
  }, [fetch])

  // Poll while any in-progress state (pending/provisioning/decommissioning)
  // so the saga timeline updates live without blasting the API on terminal
  // states.
  useEffect(() => {
    if (tenant?.status && IN_PROGRESS_STATUSES.has(tenant.status)) {
      pollRef.current = setInterval(fetch, 3000)
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [tenant?.status, fetch])

  if (!currentOrg || loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <AlertCircle className="h-8 w-8 text-muted-foreground/70 mb-3" />
        <p className="text-sm text-muted-foreground mb-4">Tenant not found.</p>
        <Link to="/dashboard/tenants" className="text-sm text-primary hover:text-primary/80">
          Back to tenants
        </Link>
      </div>
    )
  }

  const status = STATUS_LABEL[tenant.status] ?? UNKNOWN_STATUS
  const isInProgress = IN_PROGRESS_STATUSES.has(tenant.status)
  const tenantUrl = tenantBaseUrl(tenant.slug, currentOrg.slug)
  const actions: ActionAvailability = (tenant.actions as ActionAvailability) ?? {}
  // Any mutation in flight on this tenant disables EVERY action button
  // on the page. The backend dedupes via 409 but we should never allow
  // the double-click in the first place.
  const rowBusy = busyAction !== null
  // Recreate-during-decommission affordance: while the workflow is
  // running the backend rejects 409 until status flips to
  // `decommissioned`. Surface a disabled button with an explanatory
  // tooltip rather than hide-and-suddenly-reveal.
  const tenantStatus = tenant.status as string
  const showRecreatePlaceholder = tenantStatus === 'decommissioning' && !actions.can_recreate

  const runAction = async (
    name: string,
    path:
      | '/api/v1/organizations/{id}/tenants/{tenantId}/suspend'
      | '/api/v1/organizations/{id}/tenants/{tenantId}/resume'
      | '/api/v1/organizations/{id}/tenants/{tenantId}/decommission'
      | '/api/v1/organizations/{id}/tenants/{tenantId}/retry-provisioning'
      | '/api/v1/organizations/{id}/tenants/{tenantId}/cancel-provisioning'
      | '/api/v1/organizations/{id}/tenants/{tenantId}/reset-provisioning',
    body: Record<string, unknown> = {},
  ) => {
    setBusyAction(name)
    try {
      await cpClient().post(path, {
        path: { id: currentOrg.id, tenantId: tenant.id },
        body,
      })
      toast.success(`${name} started`)
      await fetch()
    } catch (err) {
      toast.error(readErr(err, `Failed to ${name.toLowerCase()}`))
    } finally {
      setBusyAction(null)
    }
  }

  // Recreate + Delete are not in the generated OpenAPI yet; call through
  // the untyped path until `make openapi-cp` catches up.
  const runRecreate = async () => {
    setBusyAction('Recreate')
    try {
      // biome-ignore lint/suspicious/noExplicitAny: untyped path
      await (cpClient() as any).post('/api/v1/organizations/{id}/tenants/{tenantId}/recreate', {
        path: { id: currentOrg.id, tenantId: tenant.id },
        body: { reason: 'Recreated via cp-org-console' },
      })
      toast.success('Recreate started')
      await fetch()
    } catch (err) {
      toast.error(readErr(err, 'Failed to recreate'))
    } finally {
      setBusyAction(null)
    }
  }

  const runDelete = async () => {
    setBusyAction('Delete')
    try {
      await cpClient().delete('/api/v1/organizations/{id}/tenants/{tenantId}', {
        path: { id: currentOrg.id, tenantId: tenant.id },
      })
      toast.success('Tenant deleted')
      navigate('/dashboard/tenants')
    } catch (err) {
      toast.error(readErr(err, 'Failed to delete tenant'))
      setBusyAction(null)
    }
  }

  return (
    <div>
      {/* Breadcrumb + header */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/dashboard/tenants')}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          All tenants
        </button>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Server className="h-6 w-6 text-muted-foreground" />
              <h1 className="text-2xl font-semibold text-foreground tracking-tight">
                {tenant.name}
              </h1>
              <span
                data-testid="tenant-status-badge"
                data-status={tenant.status}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${status.bg} ${status.text}`}
              >
                {isInProgress && <Loader2 className="h-3 w-3 animate-spin" />}
                {tenant.status === 'active' && <CheckCircle2 className="h-3 w-3" />}
                {status.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground/70 mt-1 font-mono">{tenant.slug}</p>
          </div>
          {tenant.status === 'active' && (
            <a
              href={`${tenantUrl}/admin`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
            >
              Open Admin Console
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>

      {/* Summary grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-xs text-muted-foreground/70 uppercase tracking-wider mb-2">
            Region
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Globe className="h-4 w-4 text-muted-foreground" />
            {tenant.region}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground/70 uppercase tracking-wider mb-2">Plan</div>
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Badge variant="outlined">{tenant.plan?.name ?? tenant.plan?.tier ?? '—'}</Badge>
            {tenant.deployment_topology?.has_overrides && (
              <span className="text-xs text-muted-foreground">(overrides)</span>
            )}
          </div>
        </Card>
        {/* Admin card removed 2026-06-01 — after the saga moves tenant
            admins into the per-tenant DB, the admin list will be fetched
            via a tenant-side admin API. Tracked as task #57. */}
      </div>

      {/* Provisioning timeline (the "step by step" view the user wants) */}
      <Card className="p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Workflow className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Provisioning timeline</h2>
          </div>
          {tenant.status === 'pending' && (
            <span className="text-xs text-blue-400 inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Polling...
            </span>
          )}
        </div>
        <ProvisioningTimeline
          status={tenant.status}
          provisioning={tenant.provisioning}
          allocations={tenant.resources?.allocations ?? []}
          activatedAt={tenant.activated_at}
          createdAt={tenant.created_at}
          timeline={provStatus?.timeline}
          lastError={provStatus?.last_error}
        />
      </Card>

      {/* Resources */}
      <Card className="p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Database className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">
            Allocated resources ({tenant.resources?.total ?? 0})
          </h2>
        </div>
        {(tenant.resources?.allocations ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No resources allocated yet.</p>
        ) : (
          <div className="overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-foreground/5">
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Identifier
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Mode
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[...(tenant.resources?.allocations ?? [])]
                  .sort((a, b) => {
                    const oa = RESOURCE_TYPE_ORDER[a.resource_type] ?? 99
                    const ob = RESOURCE_TYPE_ORDER[b.resource_type] ?? 99
                    return oa - ob
                  })
                  .map((alloc) => (
                    <tr key={alloc.id}>
                      <td className="px-3 py-2 text-sm text-foreground">
                        {RESOURCE_TYPE_LABEL[alloc.resource_type] ?? alloc.resource_type}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-muted-foreground">
                        {alloc.resource_id}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {alloc.topology_mode ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {formatDateTime(alloc.created_at)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Lifecycle actions — gated on actionAvailability hint booleans.
          Every button reads its enabled-state from `actions.can_*`; we
          never re-derive from status. While ANY mutation is in flight
          (`rowBusy`), every button is forced `disabled`. A handful of
          buttons surface in a disabled placeholder state for clarity
          (e.g., Decommission during provisioning, Recreate during
          decommissioning). */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-foreground mb-4">Actions</h2>
        <div className="flex flex-wrap gap-2">
          {actions.can_suspend && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={PauseCircle}
              loading={busyAction === 'Suspend'}
              disabled={rowBusy && busyAction !== 'Suspend'}
              data-testid="tenant-action-suspend"
              onClick={() =>
                runAction('Suspend', '/api/v1/organizations/{id}/tenants/{tenantId}/suspend', {
                  reason: 'Suspended via cp-org-console',
                })
              }
            >
              Suspend
            </Button>
          )}
          {actions.can_resume && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={PlayCircle}
              loading={busyAction === 'Resume'}
              disabled={rowBusy && busyAction !== 'Resume'}
              data-testid="tenant-action-resume"
              onClick={() =>
                runAction('Resume', '/api/v1/organizations/{id}/tenants/{tenantId}/resume')
              }
            >
              Resume
            </Button>
          )}
          {actions.can_retry_provisioning && !actions.can_recreate && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={Clock}
              loading={busyAction === 'Retry'}
              disabled={rowBusy && busyAction !== 'Retry'}
              data-testid="tenant-action-retry"
              onClick={() =>
                runAction(
                  'Retry',
                  '/api/v1/organizations/{id}/tenants/{tenantId}/retry-provisioning',
                )
              }
            >
              Retry provisioning
            </Button>
          )}
          {actions.can_recreate && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={RotateCw}
              loading={busyAction === 'Recreate'}
              disabled={rowBusy && busyAction !== 'Recreate'}
              data-testid="tenant-action-recreate"
              onClick={runRecreate}
            >
              Recreate
            </Button>
          )}
          {showRecreatePlaceholder && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={RotateCw}
              disabled
              title="Available after decommission completes."
              data-testid="tenant-action-recreate"
            >
              Recreate
            </Button>
          )}
          {/* TODO(docs-2026-05-17): observed in dev — this conditional
              never evaluates true during a real provisioning saga.
              Backend computes actions.can_cancel_provisioning from
              (status=provisioning && ProvisioningWorkflowID != "")
              in cp/internal/api/handlers/tenants.go computeActions(),
              but ProvisioningWorkflowID is never persisted on the
              tenant row in dev runs — so the flag stays false, the
              button never renders, the cancel feature is effectively
              unreachable from the UI. Fix is at the backend
              (persist the workflow id at the right saga step); this
              comment is a breadcrumb so FE folks know what to look
              for if they encounter the same gap. */}
          {actions.can_cancel_provisioning && (
            <Button
              variant="outline"
              size="sm"
              loading={busyAction === 'Cancel'}
              disabled={rowBusy && busyAction !== 'Cancel'}
              data-testid="tenant-action-cancel"
              onClick={() =>
                runAction(
                  'Cancel',
                  '/api/v1/organizations/{id}/tenants/{tenantId}/cancel-provisioning',
                )
              }
            >
              Cancel provisioning
            </Button>
          )}
          {actions.can_reset_provisioning && (
            <Button
              variant="outline"
              size="sm"
              loading={busyAction === 'Reset'}
              disabled={rowBusy && busyAction !== 'Reset'}
              data-testid="tenant-action-reset"
              onClick={() =>
                runAction(
                  'Reset',
                  '/api/v1/organizations/{id}/tenants/{tenantId}/reset-provisioning',
                )
              }
            >
              Reset provisioning
            </Button>
          )}
          {actions.can_decommission ? (
            <Button
              variant="destructive"
              size="sm"
              leftIcon={Trash2}
              loading={busyAction === 'Decommission'}
              disabled={rowBusy && busyAction !== 'Decommission'}
              data-testid="tenant-action-decommission"
              onClick={() =>
                runAction(
                  'Decommission',
                  '/api/v1/organizations/{id}/tenants/{tenantId}/decommission',
                  { reason: 'Decommissioned via cp-org-console' },
                )
              }
            >
              Decommission
            </Button>
          ) : tenantStatus === 'provisioning' ? (
            <Button
              variant="destructive"
              size="sm"
              leftIcon={Trash2}
              disabled
              title="Wait for provisioning to complete before decommissioning."
              data-testid="tenant-action-decommission"
            >
              Decommission
            </Button>
          ) : null}
          {actions.can_delete && (
            <Button
              variant="destructive"
              size="sm"
              leftIcon={Trash2}
              loading={busyAction === 'Delete'}
              disabled={rowBusy && busyAction !== 'Delete'}
              data-testid="tenant-action-delete"
              onClick={runDelete}
            >
              Delete
            </Button>
          )}
        </div>
      </Card>
    </div>
  )
}

// ── Provisioning timeline ─────────────────────────────────────────
//
// Two-mode renderer:
//
//   1. New mode (preferred): consumes the BE-curated `timeline` field
//      from /provisioning-status. Renders an attempt-grouped, stage-
//      grouped view with admin-facing copy from displaylabel.go and a
//      per-stage "View details" disclosure for the underlying
//      step_names (operators read those; admins don't).
//
//   2. Legacy fallback: synthesised from allocations + activatedAt.
//      Used when `timeline` is absent (older BE boot without cpEventRepo,
//      unit-test fixtures). Mostly here so we don't ship a regression
//      while environments roll forward.
function ProvisioningTimeline({
  status,
  provisioning,
  allocations,
  activatedAt,
  createdAt,
  timeline,
  lastError,
}: {
  status: TenantDetail['status']
  provisioning?: ProvisioningSummary
  allocations: ResourceAllocation[]
  activatedAt?: string
  createdAt: string
  timeline?: TimelineAttempt[]
  lastError?: string
}) {
  if (timeline && timeline.length > 0) {
    return <TimelineNew timeline={timeline} createdAt={createdAt} lastError={lastError} />
  }
  return (
    <TimelineLegacy
      status={status}
      provisioning={provisioning}
      allocations={allocations}
      activatedAt={activatedAt}
      createdAt={createdAt}
    />
  )
}

// ── New stage-grouped renderer ─────────────────────────────────────

function TimelineNew({
  timeline,
  createdAt,
  lastError,
}: {
  timeline: TimelineAttempt[]
  createdAt: string
  lastError?: string
}) {
  // Newest attempt last (BE returns oldest-first). The LATEST attempt
  // is the one the admin most likely cares about — expand it by
  // default; collapse older ones to one-line summaries.
  const sorted = [...timeline].sort((a, b) => a.attempt_number - b.attempt_number)
  const latest = sorted[sorted.length - 1]
  const earlier = sorted.slice(0, -1)
  return (
    <div className="space-y-4">
      {/* The "Tenant created" anchor stays — it's an admin-visible
          milestone that pre-dates the first saga attempt. */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground/80">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
        <span>Tenant created</span>
        <span className="text-muted-foreground/50">·</span>
        <span>{formatDateTime(createdAt)}</span>
      </div>

      {/* Earlier attempts — collapsed by default. */}
      {earlier.map((a) => (
        <AttemptCard key={a.workflow_id} attempt={a} defaultExpanded={false} />
      ))}

      {/* Latest attempt — expanded. */}
      {latest && <AttemptCard attempt={latest} defaultExpanded={true} />}

      {/* No standalone "Tenant ready" banner — the StageReady row inside
          the AttemptCard already shows the activation state, and the
          tenant-card status badge at the top of the page is the canonical
          "am I live?" signal. Two emerald checkmarks for the same fact
          read as a layout bug, not a confirmation. */}

      {/* Failure banner — separate from the stage-card failure state
          because the admin needs the reason text prominently when
          deciding whether to Retry. */}
      {lastError && (
        <div className="mt-3 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-xs">
          <div className="flex items-center gap-2 text-red-400 font-medium mb-1">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>Provisioning failed</span>
          </div>
          <p className="text-muted-foreground break-all">{lastError}</p>
        </div>
      )}
    </div>
  )
}

function AttemptCard({
  attempt,
  defaultExpanded,
}: {
  attempt: TimelineAttempt
  defaultExpanded: boolean
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const stateColor =
    attempt.state === 'completed'
      ? 'text-emerald-400'
      : attempt.state === 'failed'
        ? 'text-red-400'
        : 'text-blue-400'
  const stateLabel =
    attempt.state === 'completed'
      ? 'Succeeded'
      : attempt.state === 'failed'
        ? 'Failed'
        : 'In progress'
  return (
    <div className="border border-border/60 rounded-md">
      {/* Attempt header — always visible. Clickable so an admin can
          collapse the latest attempt too, or expand a previous one. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-foreground/[0.03] transition-colors rounded-md"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-foreground">
            Attempt {attempt.attempt_number}
          </span>
          <span className={`text-xs ${stateColor}`}>· {stateLabel}</span>
        </div>
        <span className="text-xs text-muted-foreground/70">
          {formatDateTime(attempt.started_at)}
          {attempt.ended_at ? ` → ${formatDateTime(attempt.ended_at)}` : ''}
        </span>
      </button>
      {expanded && (
        <ol className="relative border-l border-border ml-5 mr-3 mb-3 space-y-3 mt-1">
          {attempt.stages.map((stage) => (
            <StageRow key={stage.stage} stage={stage} />
          ))}
        </ol>
      )}
    </div>
  )
}

function StageRow({ stage }: { stage: TimelineStage }) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const dotBg =
    stage.status === 'failed'
      ? 'bg-red-500/20 text-red-400'
      : stage.status === 'compensating'
        ? 'bg-orange-500/20 text-orange-400'
        : stage.status === 'in_flight'
          ? 'bg-blue-500/20 text-blue-400'
          : stage.status === 'done'
            ? 'bg-emerald-500/20 text-emerald-400'
            : 'bg-muted-foreground/10 text-muted-foreground/60'
  const Icon =
    stage.status === 'failed'
      ? AlertCircle
      : stage.status === 'compensating'
        ? RotateCw
        : stage.status === 'in_flight'
          ? Loader2
          : stage.status === 'done'
            ? CheckCircle2
            : Clock
  const showSubtext = stage.subtext && stage.status !== 'done'
  return (
    <li className="ml-4">
      <span
        className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ${dotBg}`}
      >
        <Icon
          className={`h-3 w-3 ${stage.status === 'in_flight' ? 'animate-spin' : ''}`}
        />
      </span>
      <div className="flex items-baseline justify-between gap-2">
        <p
          className={`text-sm ${stage.status === 'pending' ? 'text-muted-foreground/60' : 'text-foreground'}`}
        >
          {stage.label}
        </p>
        {stage.ended_at && stage.status === 'done' && (
          <span className="text-[11px] text-muted-foreground/60 whitespace-nowrap">
            {formatDateTime(stage.ended_at)}
          </span>
        )}
      </div>
      {showSubtext && (
        <p className="text-xs text-muted-foreground/70 mt-0.5">{stage.subtext}</p>
      )}
      {stage.steps.length > 0 && (
        <button
          type="button"
          onClick={() => setDetailsOpen((v) => !v)}
          className="text-[11px] text-muted-foreground/60 hover:text-muted-foreground mt-1 inline-flex items-center gap-1"
        >
          {detailsOpen ? 'Hide details' : 'View details'}
          <span className="text-muted-foreground/40">({stage.steps.length})</span>
        </button>
      )}
      {detailsOpen && (
        <ul className="mt-2 ml-2 space-y-1 text-[11px] text-muted-foreground/80 font-mono">
          {stage.steps.map((step, i) => (
            <li key={`${step.step_name}-${i}`} className="break-all">
              <span className="text-muted-foreground/50">{formatDateTime(step.timestamp)}</span>{' '}
              <span className="text-foreground/80">{step.step_name}</span>{' '}
              <span className="text-muted-foreground/50">
                ({step.event_type.split('.').pop()})
              </span>
              {step.error && (
                <div className="text-red-400/80 ml-4 whitespace-pre-wrap">{step.error}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

// ── Legacy fallback (synthesised from allocations) ────────────────

function TimelineLegacy({
  status,
  provisioning,
  allocations,
  activatedAt,
  createdAt,
}: {
  status: TenantDetail['status']
  provisioning?: ProvisioningSummary
  allocations: ResourceAllocation[]
  activatedAt?: string
  createdAt: string
}) {
  type Event = {
    label: string
    time?: string
    icon: typeof CheckCircle2
    state: 'done' | 'in-flight' | 'failed'
    detail?: string
  }
  const events: Event[] = []
  events.push({
    label: 'Tenant created',
    time: createdAt,
    icon: CheckCircle2,
    state: 'done',
  })
  if (provisioning?.last_attempt_at) {
    events.push({
      label: `Provisioning started (attempt ${provisioning.attempt_count})`,
      time: provisioning.last_attempt_at,
      icon: Workflow,
      state: 'done',
    })
  }
  for (const alloc of [...allocations].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  )) {
    events.push({
      label: `Allocated ${RESOURCE_TYPE_LABEL[alloc.resource_type] ?? alloc.resource_type}`,
      time: alloc.created_at,
      icon: Database,
      state: 'done',
    })
  }
  if (status === 'active' && activatedAt) {
    events.push({
      label: 'Tenant ready',
      time: activatedAt,
      icon: CheckCircle2,
      state: 'done',
    })
  }
  if (status === 'pending') {
    events.push({
      label: 'Provisioning in progress…',
      icon: Loader2,
      state: 'in-flight',
    })
  }
  if (provisioning?.last_error) {
    events.push({
      label: 'Provisioning failed',
      time: provisioning.last_completed_at,
      icon: AlertCircle,
      state: 'failed',
      detail: provisioning.last_error,
    })
  }
  return (
    <ol className="relative border-l border-border ml-2 space-y-4">
      {events.map((evt, i) => {
        const Icon = evt.icon
        const dotBg =
          evt.state === 'failed'
            ? 'bg-red-500/20 text-red-400'
            : evt.state === 'in-flight'
              ? 'bg-blue-500/20 text-blue-400'
              : 'bg-emerald-500/20 text-emerald-400'
        return (
          <li key={`${evt.label}-${i}`} className="ml-4">
            <span
              className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ${dotBg}`}
            >
              <Icon className={`h-3 w-3 ${evt.state === 'in-flight' ? 'animate-spin' : ''}`} />
            </span>
            <p className="text-sm text-foreground">{evt.label}</p>
            {evt.time && (
              <p className="text-xs text-muted-foreground/70 mt-0.5">{formatDateTime(evt.time)}</p>
            )}
            {evt.detail && (
              <p className="text-xs text-muted-foreground/70 mt-0.5 break-all">{evt.detail}</p>
            )}
          </li>
        )
      })}
    </ol>
  )
}

// ── helpers ───────────────────────────────────────────────────────

function formatDateTime(s?: string): string {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return s
  }
}
